import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Clock, Edit3, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CATEGORIES } from './ideasConstants';

const formatExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const date = new Date(expiresAt);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `${diffDays} days`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
  if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months`;
  return `${Math.ceil(diffDays / 365)} years`;
};

export const IdeaCard = ({ idea, onVote, onEdit, isAuthenticated, currentUserId }) => {
  const [voting, setVoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: idea.title,
    description: idea.description,
    category: idea.category,
    subcategory: idea.subcategory || ''
  });

  const canEdit = isAuthenticated &&
                  idea.author_id === currentUserId &&
                  idea.status === 'pending';

  const handleVote = async (vote) => {
    if (!isAuthenticated) return;
    setVoting(true);
    try {
      await onVote(idea.id, vote);
    } finally {
      setVoting(false);
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await onEdit(idea.id, editData);
      setEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      title: idea.title,
      description: idea.description,
      category: idea.category,
      subcategory: idea.subcategory || ''
    });
    setEditing(false);
  };

  const currentCategory = CATEGORIES.find(c => c.id === editData.category);
  const availableSubcategories = currentCategory?.subcategories || [];

  return (
    <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5 hover:border-[#3B82F6]/30 transition-all duration-300">
      <div className="flex gap-4">
        {/* Vote buttons */}
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => handleVote(1)}
            disabled={voting || !isAuthenticated}
            className={`p-2 rounded-lg transition-all ${
              idea.user_vote === 1
                ? 'bg-[#3B82F6] text-white'
                : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56] hover:text-white'
            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ThumbsUp className="h-5 w-5" />
          </button>
          <span className={`text-lg font-bold ${idea.votes > 0 ? 'text-[#3B82F6]' : idea.votes < 0 ? 'text-[#F97316]' : 'text-gray-400'}`}>
            {idea.votes}
          </span>
          <button
            onClick={() => handleVote(-1)}
            disabled={voting || !isAuthenticated}
            className={`p-2 rounded-lg transition-all ${
              idea.user_vote === -1
                ? 'bg-[#F97316] text-white'
                : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56] hover:text-white'
            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ThumbsDown className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-3">
              <Input
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Idea title"
                className="bg-[#1e2a45] border-[#1e2a45] text-white"
              />
              <textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
                rows={2}
                className="w-full bg-[#1e2a45] border border-[#1e2a45] rounded-lg p-3 text-white text-sm focus:border-[#3B82F6] outline-none resize-none"
              />
              <div className="flex gap-2">
                <select
                  value={editData.category}
                  onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value, subcategory: '' }))}
                  className="flex-1 bg-[#1e2a45] border border-[#1e2a45] rounded-lg p-2 text-white text-sm"
                >
                  {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {availableSubcategories.length > 0 && (
                  <select
                    value={editData.subcategory}
                    onChange={(e) => setEditData(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="flex-1 bg-[#1e2a45] border border-[#1e2a45] rounded-lg p-2 text-white text-sm"
                  >
                    <option value="">Select subcategory...</option>
                    {availableSubcategories.map(sub => (
                      <option key={sub} value={sub}>{sub.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={handleCancelEdit}
                  size="sm"
                  variant="outline"
                  className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  size="sm"
                  disabled={saving || !editData.title.trim() || !editData.description.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`save-idea-${idea.id}`}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-white font-semibold text-lg line-clamp-2">{idea.title}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => setEditing(true)}
                      className="p-1.5 rounded-lg bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56] hover:text-white transition-all"
                      title="Edit idea"
                      data-testid={`edit-idea-${idea.id}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                  )}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    idea.outcome_type === 'triple'
                      ? 'bg-[#F97316]/20 text-[#F97316] border border-[#F97316]/30'
                      : 'bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30'
                  }`}>
                    {idea.outcome_type === 'triple' ? 'Win/Lose/Draw' : 'Yes/No'}
                  </span>
                </div>
              </div>

              <p className="text-gray-400 text-sm line-clamp-2 mb-3">{idea.description}</p>

              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <span className="px-2 py-1 bg-[#1e2a45] rounded-full">{idea.category}</span>
                {idea.subcategory && (
                  <span className="px-2 py-1 bg-[#1e2a45] rounded-full capitalize">{idea.subcategory.replace(/_/g, ' ')}</span>
                )}
                <span>by {idea.author_name}</span>
                <span className={`px-2 py-0.5 rounded ${
                  idea.status === 'live' ? 'bg-green-500/20 text-green-400' :
                  idea.status === 'approved' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' :
                  idea.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {idea.status}
                </span>
                {idea.expires_at && (
                  <span className="flex items-center gap-1 text-[#F97316]">
                    <Clock className="h-3 w-3" />
                    {formatExpiry(idea.expires_at)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
